import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Province, Region, SmsAdv, SmsAdvCriteriaFields, SmsAdvFields, SmsAdvLabel, ZipCodes } from '@models/smsadv/smsadv';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { formateDateTimeWithSeconds } from '@utils/utils';

@Component({
  selector: 'wsb-sms-adv-details-modal',
  templateUrl: './sms-adv-details-modal.component.html',
  styleUrls: ['./sms-adv-details-modal.component.scss'],
  standalone: false
})
export class SmsAdvDetailsModalComponent {
  @Input() public smsAdvItemsDetail: SmsAdv;
  @Input() public text;
  @Input() public labelCancelButton = 'common_close';
  @Output() dismiss: EventEmitter<any> = new EventEmitter();

  readonly STRING_EMPTY = '';
  readonly STRING_SPACE = ' ';
  readonly GENDER_ALL = 'ALL';
  readonly GENDER_MALE = 'M';
  readonly GENDER_FEMALE = 'F';

  constructor(public activeModal: NgbActiveModal, private translateService: TranslateService) {}

  get genders() {
    if (this.recipientCriteria) {
      const genders: string[] = this.recipientCriteria.genders;
      if (genders && genders.length > 0) {
        return genders.length > 1 ? this.getGendersLabel(this.GENDER_ALL) : this.getGendersLabel(genders[0]);
      }
    }
    return this.STRING_EMPTY;
  }

  get ageRanges() {
    if (this.recipientCriteria) {
      const age: string[] = this.recipientCriteria.ageRanges;
      return age && age.length > 0 ? age[0] : this.STRING_EMPTY;
    }
    return this.STRING_EMPTY;
  }

  get sendingDate() {
    return this.sendingTypeValue + this.scheduledDateValue;
  }

  get recipientsAvailable() {
    const limitRecipients = this.smsAdvItemsDetail.limitRecipients;
    const totalRecipients = this.smsAdvItemsDetail.countRecipients;
    return this.getRecipient(limitRecipients, totalRecipients);
  }

  get isRegions() {
    return this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_REGIONS);
  }

  get isProvinces() {
    return this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELDS_PROVINCES);
  }

  get isZipCodes() {
    return this.getRecipientCriteriaValue(SmsAdvCriteriaFields.FIELD_ZIP_CODES);
  }

  get regionValue() {
    const regions: Region[] = this.recipientCriteria[SmsAdvCriteriaFields.FIELD_REGIONS];
    const result = [];
    regions.forEach(r => result.push(this.getRegionRender(r)));
    return result.join();
  }

  get provincesValue() {
    const provinces: Province[] = this.recipientCriteria[SmsAdvCriteriaFields.FIELDS_PROVINCES];
    const result = [];
    provinces.forEach(pr => result.push(this.getProvincesRender(pr)));
    return result.join();
  }

  get zipCodesValue() {
    const zipCodes: ZipCodes[] = this.recipientCriteria[SmsAdvCriteriaFields.FIELD_ZIP_CODES];
    const result = [];
    zipCodes.forEach(zip => result.push(this.getZipCodesRender(zip)));
    return result.join();
  }

  // private method
  private get sendingTypeValue() {
    if (!this.smsAdvItemsDetail) {
      return this.STRING_EMPTY;
    }
    const sendingType: string = this.smsAdvItemsDetail[SmsAdvFields.FIELD_SENDING_TYPE];
    if (sendingType) {
      const labels = SmsAdvLabel.SUMMARY_SEND_TYPE + sendingType.toLowerCase();
      return this.getTranslateLabels(labels);
    }
    return this.STRING_EMPTY;
  }

  private get scheduledDateValue() {
    if (!this.smsAdvItemsDetail) {
      return this.STRING_EMPTY;
    }
    const scheduledDate = this.smsAdvItemsDetail[SmsAdvFields.FIELD_SCHEDULED_DATE];
    if (scheduledDate) {
      return ' (' + this.getFormatScheduledDate(scheduledDate) + ')';
    }
    return this.STRING_EMPTY;
  }

  private get recipientCriteria() {
    const criteria = this.smsAdvItemsDetail.recipientCriteria;
    return criteria ? criteria : undefined;
  }

  private getGendersLabel(value) {
    switch (value) {
      case this.GENDER_MALE:
        return 'smsadv_gender_male';
      case this.GENDER_FEMALE:
        return 'smsadv_gender_female';
      case this.GENDER_ALL:
        return 'smsadv_gender_all';
      default:
        return this.STRING_EMPTY;
    }
  }

  private getRecipient(limit, total) {
    if (limit && limit > 0) {
      return limit;
    } else if (total && total > 0) {
      return total;
    }
    return 0;
  }

  private getFormatScheduledDate(scheduledDate) {
    const dateTime = formateDateTimeWithSeconds(scheduledDate).split(' ');
    const date = dateTime[0];
    const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
    return date + ' ' + time;
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private getRegionRender(region: Region) {
    return this.STRING_SPACE + region.name;
  }

  private getProvincesRender(province: Province) {
    return this.STRING_SPACE + province.abbreviation;
  }

  private getZipCodesRender(zipCode: ZipCodes) {
    return this.STRING_SPACE + zipCode.zipCode;
  }

  private getRecipientCriteriaValue(field: string) {
    if (this.recipientCriteria && this.recipientCriteria[field]) {
      return this.recipientCriteria[field];
    }
    return undefined;
  }
}
